import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { ArrowRight, Sparkles, Leaf, Award, Truck, Heart, Factory, Shield, Star, Zap, GraduationCap } from "lucide-react";
import { api, LOGO_URL } from "@/lib/api";
import { useContent, t, parseHiddenSet } from "@/hooks/useContent";
import Seo from "@/components/Seo";

const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };
const item = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

export default function Home() {
  const [products, setProducts] = useState([]);
  const c = useContent();
  const hidden = parseHiddenSet(c);
  const H = (id) => hidden.has(id);
  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <Seo
        path="/"
        title="B-Raw — Tasty & Crunchy Snacks | Made in Sirsa"
        description="B-Raw brings you hand-crafted, FSSAI-certified snacks from Sirsa. 5 signature flavours at ₹10 a pack — real ingredients, honest pricing, small-batch made in Bharat."
      />
      {/* HERO */}
      {!H("home.hero.section") && (
      <section className="relative overflow-hidden sunburst-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 pb-16 sm:pb-24 grid md:grid-cols-12 gap-8 items-center">
          <motion.div
            variants={stagger}
            initial="hidden"
            animate="show"
            className="md:col-span-7"
          >
            {!H("home.hero.tagline") && (
              <motion.div variants={item} className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#111] text-[#F2A900] text-xs font-bold uppercase tracking-[0.2em] rounded-full">
                <Sparkles size={14} /> {t(c, "home.hero.tagline", "Made in Sirsa · Loved in Bharat")}
              </motion.div>
            )}
            {!(H("home.hero.title_1") && H("home.hero.title_2") && H("home.hero.title_3")) && (
              <motion.h1
                variants={item}
                data-testid="hero-title"
                className="mt-5 font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter text-[#111]"
              >
                {!H("home.hero.title_1") && <>{t(c, "home.hero.title_1", "Tasty &")} </>}
                {!H("home.hero.title_2") && <span className="text-[#D9381E]">{t(c, "home.hero.title_2", "Crunchy")}</span>}
                <br />
                {!H("home.hero.title_3") && t(c, "home.hero.title_3", "in every bite.")}
              </motion.h1>
            )}
            {!H("home.hero.subtitle") && (
              <motion.p variants={item} className="mt-6 text-base md:text-lg text-[#4B5563] max-w-xl leading-relaxed">
                {t(c, "home.hero.subtitle", "B-Raw is a hand-crafted Indian snack brand built by food engineer Devender Yadav. Real spices, honest ingredients, and small-batch, FSSAI-certified quality in every pack.")}
              </motion.p>
            )}
            <motion.div variants={item} className="mt-8 flex flex-wrap items-center gap-3">
              {!H("home.hero.cta_primary") && (
                <Link
                  to="/products"
                  data-testid="hero-cta-products"
                  className="inline-flex items-center gap-2 bg-[#D9381E] hover:bg-[#B02C15] text-white px-6 py-3.5 rounded-full font-bold shadow-[0_10px_28px_rgba(217,56,30,0.3)] transition-colors"
                >
                  {t(c, "home.hero.cta_primary", "Shop Our Range")} <ArrowRight size={18} />
                </Link>
              )}
              {!H("home.hero.cta_secondary") && (
                <Link
                  to="/about"
                  data-testid="hero-cta-secondary"
                  className="inline-flex items-center gap-2 border-2 border-[#111] text-[#111] px-6 py-3.5 rounded-full font-bold hover:bg-[#111] hover:text-[#F2A900] transition-colors"
                >
                  {t(c, "home.hero.cta_secondary", "Meet the Founder")} <ArrowRight size={18} />
                </Link>
              )}
            </motion.div>
            {!H("home.hero.stats") && (
              <motion.div variants={item} className="mt-10 grid grid-cols-3 gap-6 max-w-md" data-testid="hero-stats">
                {parseStats(t(c, "home.hero.stats", "")).filter((s) => !s.hidden).map((s, i) => (
                  <Stat key={i} n={s.n} l={s.l} />
                ))}
              </motion.div>
            )}
          </motion.div>

          {!H("home.hero.image") && (
            <div className="md:col-span-5 relative">
              <div className="absolute inset-0 -z-10 spin-slow opacity-40">
                <div className="w-72 h-72 sm:w-96 sm:h-96 rounded-full bg-gradient-to-tr from-[#F2A900] to-[#D9381E] mx-auto" />
              </div>
              <motion.img
                initial={{ opacity: 0, scale: 0.85, rotate: -10 }}
                animate={{ opacity: 1, scale: 1, rotate: -6 }}
                transition={{ duration: 0.8 }}
                src={t(c, "home.hero.image", LOGO_URL)}
                alt="B-Raw"
                className="w-full max-w-md mx-auto aspect-square object-contain drop-shadow-[0_20px_40px_rgba(217,56,30,0.25)]"
              />
            </div>
          )}
        </div>
      </section>
      )}

      {/* USP STRIP */}
      {!H("home.usp.section") && (
      <section className="bg-[#111] text-[#FDFBF7] py-8 overflow-hidden" data-testid="usp-marquee">
        <div className="flex marquee-track gap-14 whitespace-nowrap text-2xl sm:text-3xl font-display font-black">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex gap-14 items-center shrink-0">
              {parseUsp(t(c, "home.usp.marquee", "")).filter((p) => !p.hidden).map((phrase, j) => (
                <span key={j} className="flex gap-14 items-center">
                  <span>{phrase.text}</span>
                  <span className="text-[#F2A900]">★</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>
      )}

      {/* PRODUCTS */}
      {!H("home.products.section") && (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            {!H("home.products.eyebrow") && (
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D9381E]">
                {t(c, "home.products.eyebrow", "Our range")}
              </div>
            )}
            {!H("home.products.title") && (
              <h2 className="mt-2 font-display font-bold text-4xl sm:text-5xl tracking-tight text-[#111]">
                {t(c, "home.products.title", "Snacks that speak your language.")}
              </h2>
            )}
          </div>
          <Link
            to="/products"
            data-testid="see-all-products"
            className="text-sm font-bold text-[#111] underline underline-offset-4 hover:text-[#D9381E]"
          >
            See all products →
          </Link>
        </div>

        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {products.filter((p) => !H(`product:${p.id}`)).slice(0, 3).map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="group relative rounded-3xl overflow-hidden border border-black/10"
              style={{ backgroundColor: p.bg }}
              data-testid={`home-product-${p.id}`}
            >
              <div className="p-6 pb-0">
                <div className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: p.color }}>
                  {p.price} pouch
                </div>
                <h3 className="mt-2 font-display font-bold text-3xl tracking-tight" style={{ color: p.color }}>
                  {p.name}
                </h3>
                <p className="mt-2 text-sm text-black/60 line-clamp-2">{p.tagline}</p>
              </div>
              <div className="aspect-[4/3] flex items-end justify-center">
                <img
                  src={p.image}
                  alt={p.name}
                  className="w-full h-full object-contain object-bottom p-4 transition-transform duration-500 group-hover:-translate-y-2"
                />
              </div>
            </motion.div>
          ))}
        </div>
      </section>
      )}

      {/* WHY */}
      {!H("home.why.section") && (
      <section className="bg-[#F3EAD3] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D9381E]">
              Why B-Raw
            </div>
            {!H("home.why.title") && (
              <h2 className="mt-2 font-display font-bold text-4xl sm:text-5xl tracking-tight text-[#111]">
                {t(c, "home.why.title", "A snack you can actually trust.")}
              </h2>
            )}
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-4" data-testid="why-features">
            {parseFeatures(t(c, "home.why.features", "")).filter((f) => !f.hidden).map((f, i) => (
              <Feature key={i} icon={iconFor(f.icon)} title={f.title} text={f.text} />
            ))}
          </div>
        </div>
      </section>
      )}

    </div>
  );
}

function Stat({ n, l }) {
  return (
    <div>
      <div className="font-display font-black text-3xl sm:text-4xl text-[#111]">{n}</div>
      <div className="mt-1 text-xs uppercase tracking-widest text-[#6B7280]">{l}</div>
    </div>
  );
}

function parseStats(raw) {
  const fallback = [
    { n: "9+", l: "Yrs Experience" },
    { n: "5", l: "Signature Flavours" },
    { n: "₹5", l: "Pocket-friendly" },
  ];
  if (!raw) return fallback;
  try {
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return fallback;
    return arr.filter((s) => s && (s.n || s.l)).map((s) => ({ n: s.n, l: s.l, hidden: !!s.hidden }));
  } catch {
    return fallback;
  }
}

function parseUsp(raw) {
  const fallback = [
    { text: "TASTY & CRUNCHY", hidden: false },
    { text: "HAND-CRAFTED IN SIRSA", hidden: false },
    { text: "FSSAI CERTIFIED", hidden: false },
    { text: "SMALL-BATCH FRESH", hidden: false },
    { text: "PROUDLY BHARAT", hidden: false },
  ];
  if (!raw) return fallback;
  try {
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return fallback;
    const clean = arr.map((s) => {
      if (typeof s === "string") return { text: s.trim(), hidden: false };
      return { text: String(s?.text ?? s?.value ?? "").trim(), hidden: !!s?.hidden };
    }).filter((s) => s.text);
    return clean.length ? clean : fallback;
  } catch {
    return fallback;
  }
}

function parseFeatures(raw) {
  const fallback = [
    { icon: "leaf", title: "Real Ingredients", text: "No shortcuts. Every pack lists exactly what's inside." },
    { icon: "award", title: "Food-Engineered", text: "Recipes by a BTech food engineer with 9+ years in the industry." },
    { icon: "shield", title: "FSSAI Certified", text: "Every pouch meets India's food-safety standards — no compromises." },
    { icon: "truck", title: "Fresh from Sirsa", text: "Small-batch production, direct-to-market freshness." },
  ];
  if (!raw) return fallback;
  try {
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return fallback;
    return arr.map((f) => ({ icon: f?.icon || "sparkles", title: f?.title || "", text: f?.text || "", hidden: !!f?.hidden }));
  } catch {
    return fallback;
  }
}

function iconFor(name) {
  const map = {
    leaf: <Leaf />, award: <Award />, truck: <Truck />,
    sparkles: <Sparkles />, heart: <Heart />, factory: <Factory />, shield: <Shield />,
    star: <Star />, zap: <Zap />, graduationcap: <GraduationCap />,
  };
  return map[String(name || "").toLowerCase()] || <Sparkles />;
}

function Feature({ icon, title, text }) {
  return (
    <div className="p-6 bg-[#FDFBF7] rounded-2xl border border-black/10">
      <div className="h-11 w-11 rounded-xl bg-[#111] text-[#F2A900] flex items-center justify-center">
        {icon}
      </div>
      <div className="mt-4 font-display font-bold text-xl text-[#111]">{title}</div>
      <p className="mt-2 text-sm text-[#4B5563] leading-relaxed">{text}</p>
    </div>
  );
}
